import type { FC, ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, Drawer, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Done as DoneIcon,
  EmojiObjectsOutlined as EmojiObjectsOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  LocalFireDepartmentOutlined as LocalFireDepartmentOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  SellOutlined as SellOutlinedIcon,
  SentimentSatisfiedAltOutlined as SentimentSatisfiedAltOutlinedIcon,
  TheatersOutlined as TheatersOutlinedIcon,
} from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import RedditCommentItem from './RedditCommentItem';
import RedditPostBody from './RedditPostBody';
import type { RedditPost, RedditTone } from '../types';

interface RedditPostDetailsDrawerProps {
  post: RedditPost | null;
  onClose: () => void;
}

interface SectionHeaderProps {
  title: string;
  icon: ReactNode;
  color: string;
}

interface CopyActionButtonProps {
  label: string;
  value: string;
  copiedValue: string | null;
  onCopy: (value: string) => void;
}

interface SectionListProps {
  items: string[];
  onCopy: (value: string) => void;
  copiedValue: string | null;
}

interface SectionHeaderWithActionProps extends SectionHeaderProps {
  action?: ReactNode;
}

interface ToneSectionProps {
  tone: RedditTone | null;
}

interface CommentsAccordionProps {
  comments: RedditPost['comments'];
}

const formatCount = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const formatDate = (value: string, pattern: string): string => {
  if (!value) return '';
  const date = parseISO(value);
  return isValid(date) ? format(date, pattern) : '';
};

const useCopyFeedback = () => {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const onCopy = useCallback((value: string) => {
    void navigator.clipboard.writeText(value);
    setCopiedValue(value);

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setCopiedValue(null);
      timeoutRef.current = null;
    }, 1500);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  return { copiedValue, onCopy };
};

const SectionHeader = memo(({ title, icon, color }: SectionHeaderProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ display: 'inline-flex', color }}>
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 700,
        color,
      }}
    >
      {title}
    </Typography>
  </Box>
));

SectionHeader.displayName = 'SectionHeader';

const SectionHeaderWithAction = memo(({ title, icon, color, action }: SectionHeaderWithActionProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
    <SectionHeader title={title} icon={icon} color={color} />
    {action ?? null}
  </Box>
));

SectionHeaderWithAction.displayName = 'SectionHeaderWithAction';

const CopyActionButton = memo(({ label, value, copiedValue, onCopy }: CopyActionButtonProps) => (
  <Tooltip title={copiedValue === value ? 'Copied!' : 'Copy'}>
    <IconButton size="small" onClick={() => { onCopy(value); }} aria-label={`Copy ${label}`}>
      {copiedValue === value ? (
        <DoneIcon sx={{ fontSize: 16, color: 'success.main' }} />
      ) : (
        <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      )}
    </IconButton>
  </Tooltip>
));

CopyActionButton.displayName = 'CopyActionButton';

const EmptySectionState = memo(() => (
  <Typography variant="body2" color="text.secondary">
    No data yet.
  </Typography>
));

EmptySectionState.displayName = 'EmptySectionState';

const PainPointSection = memo(({ items, onCopy, copiedValue }: SectionListProps) => (
  <Box sx={{ mt: 3 }}>
    {items.length === 0 ? (
      <>
        <SectionHeader title="Pain point" icon={<LocalFireDepartmentOutlinedIcon sx={{ fontSize: 14 }} />} color="#E57373" />
        <EmptySectionState />
      </>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, index) => (
          <Box
            key={`${item}-${index}`}
            sx={(t) => ({
              p: 1.5,
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              backgroundColor: t.palette.background.paper,
            })}
          >
            <SectionHeaderWithAction
              title="Pain point"
              icon={<LocalFireDepartmentOutlinedIcon sx={{ fontSize: 14 }} />}
              color="#E57373"
              action={<CopyActionButton label="pain point" value={item} copiedValue={copiedValue} onCopy={onCopy} />}
            />
            <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
));

PainPointSection.displayName = 'PainPointSection';

const ToneSection = memo(({ tone }: ToneSectionProps) => (
  <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <SectionHeader title="Tone" icon={<SentimentSatisfiedAltOutlinedIcon sx={{ fontSize: 14 }} />} color="#1E88E5" />
    {tone === null ? (
      <EmptySectionState />
    ) : (
      <Chip label={tone} size="small" variant="outlined" sx={{ borderColor: '#1E88E5', color: '#1E88E5' }} />
    )}
  </Box>
));

ToneSection.displayName = 'ToneSection';

const HookIdeasSection = memo(({ items, onCopy, copiedValue }: SectionListProps) => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ mb: 1.25 }}>
      <SectionHeader title="Hook ideas" icon={<EmojiObjectsOutlinedIcon sx={{ fontSize: 14 }} />} color="#EF6C00" />
    </Box>
    {items.length === 0 ? (
      <EmptySectionState />
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((idea, index) => (
          <Box
            key={`${idea}-${index}`}
            sx={(t) => ({
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 1.5,
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              backgroundColor: t.palette.background.paper,
            })}
          >
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 16, mt: 0.15, fontWeight: 600 }}>
              {index + 1}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.6 }}>
              {idea}
            </Typography>
            <CopyActionButton label="hook idea" value={idea} copiedValue={copiedValue} onCopy={onCopy} />
          </Box>
        ))}
      </Box>
    )}
  </Box>
));

HookIdeasSection.displayName = 'HookIdeasSection';

const CreativeScriptsSection = memo(({ items, onCopy, copiedValue }: SectionListProps) => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ mb: 1.25 }}>
      <SectionHeader title="Creative scripts" icon={<TheatersOutlinedIcon sx={{ fontSize: 14 }} />} color="#8E24AA" />
    </Box>
    {items.length === 0 ? (
      <EmptySectionState />
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((script, index) => (
          <Box
            key={`${script}-${index}`}
            sx={(t) => ({
              p: 1.5,
              border: `1px solid ${t.palette.divider}`,
              borderLeft: '3px solid #8E24AA',
              borderRadius: 2,
              backgroundColor: t.palette.background.paper,
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                  Script {index + 1}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {script}
                </Typography>
              </Box>
              <CopyActionButton label="creative script" value={script} copiedValue={copiedValue} onCopy={onCopy} />
            </Box>
          </Box>
        ))}
      </Box>
    )}
  </Box>
));

CreativeScriptsSection.displayName = 'CreativeScriptsSection';

const KeyPhrasesSection = memo(({ items, onCopy, copiedValue }: SectionListProps) => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ mb: 1.25 }}>
      <SectionHeader title="Key phrases" icon={<SellOutlinedIcon sx={{ fontSize: 14 }} />} color="#37474F" />
    </Box>
    {items.length === 0 ? (
      <EmptySectionState />
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {items.map((phrase, index) => (
          <Box
            key={`${phrase}-${index}`}
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              backgroundColor: t.palette.background.paper,
            })}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {phrase}
            </Typography>
            <CopyActionButton label="key phrase" value={phrase} copiedValue={copiedValue} onCopy={onCopy} />
          </Box>
        ))}
      </Box>
    )}
  </Box>
));

KeyPhrasesSection.displayName = 'KeyPhrasesSection';

const CommentsAccordion = memo(({ comments }: CommentsAccordionProps) => (
  <Accordion disableGutters sx={{ mt: 3, border: 'none', boxShadow: 'none' }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Top comments ({comments.length})
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pt: 0.5 }}>
      {comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No comments yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {comments.map((comment, index) => (
            <Box key={comment.reddit_id}>
              <RedditCommentItem comment={comment} />
              {index < comments.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      )}
    </AccordionDetails>
  </Accordion>
));

CommentsAccordion.displayName = 'CommentsAccordion';

const RedditPostDetailsDrawer: FC<RedditPostDetailsDrawerProps> = ({ post, onClose }) => {
  const comments = post?.comments ?? [];
  const { copiedValue, onCopy } = useCopyFeedback();

  return (
    <Drawer
      anchor="right"
      open={post !== null}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 480 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${t.palette.divider}`,
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
        })}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {post?.subreddit}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close details">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {post && (
          <>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1, lineHeight: 1.3 }}>
              {post.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {post.author}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ·
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(post.posted_at, 'MMM d, yyyy')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                <ArrowUpwardIcon sx={{ fontSize: 14, color: '#FF4500' }} />
                <Typography variant="caption" fontWeight={600}>
                  {formatCount(post.upvotes_count)}
                </Typography>
              </Box>
            </Box>

            <RedditPostBody body={post.body} />

            <Button
              component="a"
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ mt: 2, textTransform: 'none', fontSize: '0.8125rem' }}
            >
              Open on Reddit
            </Button>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" fontWeight={600}>AI Analysis</Typography>
            <PainPointSection items={post.pain_point} copiedValue={copiedValue} onCopy={onCopy} />
            <ToneSection tone={post.tone} />
            <HookIdeasSection items={post.hook_ideas} copiedValue={copiedValue} onCopy={onCopy} />
            <CreativeScriptsSection items={post.creative_scripts} copiedValue={copiedValue} onCopy={onCopy} />
            <KeyPhrasesSection items={post.key_phrases} copiedValue={copiedValue} onCopy={onCopy} />
            <CommentsAccordion comments={comments} />
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default RedditPostDetailsDrawer;
